<template>
    <div class="py-12">
        <CrumbsBar :domain="reportedDomain.domain" :source="source"></CrumbsBar>

        <div>
            <StatsBarDetail
                :stats="{ pass: reports.total.pass, fail: reports.total.fail }"
                :stats-before="null"
            ></StatsBarDetail>
        </div>

        <StatsChart
            v-if="reports.total.pass > 0 || reports.total.fail > 0"
            :chart-data="reports.chartData"
            :header="t('vue.daily_reports_from_this_sender')"
        ></StatsChart>

        <div class="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div class="rounded-[12.8px] border border-gray-200 bg-white p-6">
                <h3 class="mb-6 font-medium text-gray-900">
                    {{ $t("vue.source_insights") }}
                </h3>
                <div class="space-y-4">
                    <div
                        class="flex items-start gap-4 rounded-lg border border-green-100 bg-green-50 p-4"
                        :class="{
                            'border-yellow-100 bg-yellow-50':
                                totalCompliance < 75 && totalCompliance > 50,
                            'border-red-100 bg-red-50': totalCompliance <= 50,
                        }"
                    >
                        <div
                            class="mt-2 h-2 w-2 shrink-0 rounded-full bg-green-500"
                            :class="{
                                'bg-yellow-500':
                                    totalCompliance < 75 &&
                                    totalCompliance > 50,
                                'bg-red-500': totalCompliance <= 50,
                            }"
                        ></div>
                        <div>
                            <p class="mb-1 font-medium text-green-900">
                                {{ generateDmarcSummary().headline }}
                            </p>
                            <p class="text-sm text-green-800">
                                {{ generateDmarcSummary().summary }}
                            </p>
                        </div>
                    </div>
                    <div
                        class="flex items-start gap-4 rounded-lg border border-blue-100 bg-blue-50 p-4"
                    >
                        <div
                            class="mt-2 h-2 w-2 shrink-0 rounded-full bg-blue-500"
                        ></div>
                        <div>
                            <p class="mb-1 font-medium text-blue-900">
                                Primary IPs
                            </p>
                            <p class="text-sm text-blue-800">
                                {{ getPrimaryIpVolumeSummary() }}
                            </p>
                        </div>
                    </div>
                    <div
                        v-if="getSpfAlignmentWarning() !== null"
                        class="flex items-start gap-4 rounded-lg border border-yellow-100 bg-yellow-50 p-4"
                    >
                        <div
                            class="mt-2 h-2 w-2 shrink-0 rounded-full bg-yellow-500"
                        ></div>
                        <div>
                            <p class="mb-1 font-medium text-yellow-900">
                                SPF Alignment
                            </p>
                            <p class="text-sm text-yellow-800">
                                {{ getSpfAlignmentWarning() }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="rounded-[12.8px] border border-gray-200 bg-white p-6">
                <h3 class="mb-6 font-medium text-gray-900">
                    {{ $t("vue.source_summary") }}
                </h3>
                <div class="space-y-4">
                    <div class="flex items-center justify-between py-2">
                        <span class="text-gray-600">{{
                            $t("vue.active_ip_addresses")
                        }}</span
                        ><span class="font-medium text-gray-900">{{
                            Object.keys(reports.ips).length
                        }}</span>
                    </div>
                    <div class="flex items-center justify-between py-2">
                        <span class="text-gray-600">{{
                            $t("vue.average_dmarc_compliance")
                        }}</span
                        ><span
                            class="font-medium text-green-600"
                            :style="{
                                color: calculateColorByPercents(
                                    calculateCompliance(
                                        reports.total.pass,
                                        reports.total.fail,
                                    ),
                                ),
                            }"
                            >{{
                                calculateCompliance(
                                    reports.total.pass,
                                    reports.total.fail,
                                )
                            }}</span
                        >
                    </div>
                    <div class="flex items-center justify-between py-2">
                        <span class="text-gray-600">{{
                            $t("vue.peak_volume")
                        }}</span
                        ><span class="font-medium text-gray-900"
                            >{{
                                new Intl.NumberFormat().format(
                                    peakDay.pass + peakDay.fail,
                                )
                            }}
                            emails</span
                        >
                    </div>
                    <div class="flex items-center justify-between py-2">
                        <span class="text-gray-600">{{
                            $t("vue.authentication_method")
                        }}</span
                        ><span class="font-medium text-gray-900">{{
                            getPrimaryAuthMethod()
                        }}</span>
                    </div>
                    <div class="flex hidden items-center justify-between py-2">
                        <span class="text-gray-600">{{
                            $t("vue.last_activity")
                        }}</span
                        ><span class="font-medium text-gray-900"
                            >3 {{ $t("vue.days_ago") }}</span
                        >
                    </div>
                </div>
            </div>
        </div>

        <div
            class="mt-5 overflow-hidden rounded-[12.8px] border border-gray-200 bg-white"
        >
            <div class="border-b border-gray-100 px-6 py-5">
                <h3 class="font-medium text-gray-900">
                    {{ $t("vue.ip_address_details") }}
                </h3>
                <p class="mt-1 text-sm text-gray-600">
                    {{ $t("vue.auth_and_compliance_metrics_by_ip_address") }}
                </p>
            </div>
            <div class="overflow-x-auto">
                <table class="w-full">
                    <thead class="border-b border-gray-100 bg-gray-50">
                        <tr>
                            <th
                                class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                            >
                                {{ $t("vue.ip_address") }}
                            </th>
                            <th
                                class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                            >
                                {{ $t("vue.emails_reported") }}
                            </th>
                            <th
                                class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                            >
                                {{ $t("vue.dmarc_compliance") }}
                            </th>
                            <th
                                class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                            >
                                {{ $t("vue.spf_aligned") }}
                            </th>
                            <th
                                class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                            >
                                {{ $t("vue.dkim_aligned") }}
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr
                            v-for="row in reports.ips"
                            :key="row.ip"
                            class="transition-colors duration-150 hover:bg-gray-50"
                        >
                            <td class="px-6 py-4">
                                <a
                                    :href="
                                        route('reports.show.ip', {
                                            domain: reportedDomain.domain,
                                            source: source,
                                            ip: row.ip,
                                        })
                                    "
                                    class="group/domain -m-2 flex cursor-pointer items-center gap-3 rounded-lg p-2 text-left font-mono transition-colors duration-150 hover:bg-blue-50"
                                >
                                    <span
                                        class="font-medium text-blue-600 transition-colors duration-150 hover:text-blue-800 group-hover/domain:underline"
                                        >{{ row.ip }}</span
                                    >
                                </a>
                            </td>
                            <td class="px-6 py-4">
                                <span class="text-gray-900">{{
                                    new Intl.NumberFormat().format(row.sum)
                                }}</span>
                            </td>
                            <td class="px-6 py-4">
                                <span
                                    class="font-medium text-green-600"
                                    :style="{
                                        color: calculateColorByPercents(
                                            calculateCompliance(
                                                row.pass,
                                                row.fail,
                                            ),
                                        ),
                                    }"
                                    >{{
                                        calculateCompliance(row.pass, row.fail)
                                    }}</span
                                >
                            </td>
                            <td class="px-6 py-4">
                                <span
                                    class="font-medium text-red-600"
                                    :style="{
                                        color: calculateColorByPercents(
                                            calculateCompliance(
                                                row.spf_valid,
                                                row.spf_invalid,
                                            ),
                                        ),
                                    }"
                                    >{{
                                        calculateCompliance(
                                            row.spf_valid,
                                            row.spf_invalid,
                                        )
                                    }}</span
                                >
                            </td>
                            <td class="px-6 py-4">
                                <span
                                    class="font-medium text-green-600"
                                    :style="{
                                        color: calculateColorByPercents(
                                            calculateCompliance(
                                                row.dkim_valid,
                                                row.dkim_invalid,
                                            ),
                                        ),
                                    }"
                                    >{{
                                        calculateCompliance(
                                            row.dkim_valid,
                                            row.dkim_invalid,
                                        )
                                    }}</span
                                >
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type ISelectable from "@/types/Atomic/ISelectable";
import type { PropType } from "vue";
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations.ts";

const { t } = useTranslations();

const props = defineProps({
    reports: {
        type: Array as PropType<ISelectable[]>,
        default: () => [],
    },
    source: {
        type: String,
        default: () => "",
    },
    reportedDomain: {
        type: String,
        default: () => "",
    },
});

const peakDay = computed(() => {
    return props.reports.chartData.reduce((peak, current) => {
        const currentTotal = current.pass + current.fail;
        const peakTotal = peak.pass + peak.fail;
        return currentTotal > peakTotal ? current : peak;
    });
});

const getPrimaryIpVolumeSummary = () => {
    const entries = Object.entries(props.reports.ips).sort(
        (a, b) => b[1].sum - a[1].sum,
    );
    const totalVolume = entries.reduce(
        (acc, [_, val]) => acc + (val.sum || 0),
        0,
    );

    let runningVolume = 0;
    let count = 0;

    for (const [_, val] of entries) {
        runningVolume += val.sum || 0;
        count++;
        if (runningVolume / totalVolume >= 0.7 || count >= 10) break;
    }

    const percent = totalVolume
        ? ((runningVolume / totalVolume) * 100).toFixed(1)
        : "0.0";

    if (parseFloat(percent) < 60) {
        return "Traffic is evenly distributed across IP addresses.";
    }

    return `Top ${count} IP addresses handle ${percent}% of all email volume.`;
};

const getSpfAlignmentWarning = () => {
    const totalIps = Object.keys(props.reports.ips).length;
    if (totalIps === 0) return null;

    const zeroSpfIps = Object.values(props.reports.ips).filter(
        (ip) => (ip.spf_valid || 0) === 0 && (ip.spf_invalid || 0) > 0,
    ).length;

    const percent = ((zeroSpfIps / totalIps) * 100).toFixed(1);

    if (zeroSpfIps / totalIps < 0.1) return null;

    return `${percent}% of IPs show 0% SPF alignment — consider reviewing your SPF records.`;
};

const totalCompliance = () => {
    return props.reports.total.pass + props.reports.total.fail > 0
        ? Math.floor(
              (props.reports.total.pass /
                  (props.reports.total.pass + props.reports.total.fail)) *
                  100,
          )
        : 0;
};

const generateDmarcSummary = () => {
    let dmarcPassRate = totalCompliance();

    let headline;

    if (dmarcPassRate >= 99) {
        headline = "Excellent Performance";
    } else if (dmarcPassRate >= 95) {
        headline = "Good Performance";
    } else if (dmarcPassRate >= 80) {
        headline = "Moderate Performance";
    } else {
        headline = "Needs Attention";
    }

    const formatted = (value) => value.toFixed(1).replace(/\.0$/, "") + "%";

    return {
        headline: headline,
        summary: `${formatted(
            dmarcPassRate,
        )} DMARC compliance rate across all IP addresses.`,
    };
};

const calculateCompliance = (pass, fail) => {
    return pass + fail > 0
        ? Math.floor((pass / (pass + fail)) * 100) + "%"
        : 0 + "%";
};

const getPrimaryAuthMethod = () => {
    let spfValid = 0,
        spfTotal = 0;
    let dkimValid = 0,
        dkimTotal = 0;

    for (const ip of Object.values(props.reports.ips)) {
        spfValid += ip.spf_valid || 0;
        spfTotal += (ip.spf_valid || 0) + (ip.spf_invalid || 0);
        dkimValid += ip.dkim_valid || 0;
        dkimTotal += (ip.dkim_valid || 0) + (ip.dkim_invalid || 0);
    }

    const spfRate = spfTotal ? spfValid / spfTotal : 0;
    const dkimRate = dkimTotal ? dkimValid / dkimTotal : 0;

    const delta = Math.abs(spfRate - dkimRate);

    if (delta < 0.05) {
        return "Balanced SPF & DKIM";
    } else if (dkimRate > spfRate) {
        return "DKIM Primary";
    } else {
        return "SPF Primary";
    }
};

const calculateColorByPercents = (percents) => {
    var percentNum = parseInt(percents.replace("%", ""));

    if (percentNum > 75) {
        return "#15803D";
    }

    if (percentNum > 50) {
        return "#CA8A04";
    }

    return "#B91C1C";
};
</script>
